import { api, getCachedWorkspace, saveWorkspaceToConfig } from './api-client'
import { getGitRemote } from './git'

export type ResolutionSource = 'config' | 'me' | 'git-org'

export interface ResolvedRepo {
  workspaceSlug: string
  repoId: string
  remote: { org: string; repo: string }
  /** Where the winning slug came from — surfaced so agents/users can see
   *  whether resolution used the cache, a live /me, or the git-org fallback. */
  source: ResolutionSource
}

/**
 * Why this exists: the workspace slug (e.g. `nippy-1778624457980832055`) is a
 * different namespace from the GitHub org (e.g. `Nippy-Tech`). The API's
 * workspace guard rejects any slug that isn't exactly the one tied to the API
 * key, so the git org is NOT a valid slug unless it happens to equal it. The
 * only authoritative sources are the cached config slug and `/me`. These
 * diagnostics make it explicit which sources were tried and why they failed,
 * instead of silently falling back to a slug that can never resolve.
 */
export interface RepoDiagnostics {
  remote?: { org: string; repo: string }
  triedSlugs: string[]
  configSlug?: string
  meAttempted: boolean
  meReturned: boolean
  meSlug?: string
  meWorkspaceId?: string
  meError?: string
  failedEndpoint?: string
}

export type RepoResolution =
  | ({ ok: true } & ResolvedRepo)
  | {
      ok: false
      reason: 'no_remote' | 'no_workspace' | 'not_linked'
      workspaceSlug?: string
      remote?: { org: string; repo: string }
      detail?: string
      diagnostics?: RepoDiagnostics
    }

export function notLinkedMessage(remote: { org: string; repo: string }, workspaceSlug: string): string {
  return `Repo '${remote.org}/${remote.repo}' is not registered in workspace '${workspaceSlug}'. Run \`driftless doctor\` to diagnose, or create topics without linking.`
}

/** Multi-line, copy-pasteable diagnostic for the no_workspace failure. */
export function formatDiagnostics(d: RepoDiagnostics): string {
  const lines: string[] = []
  if (d.remote) lines.push(`  repo:        ${d.remote.org}/${d.remote.repo}`)
  lines.push(`  tried slugs: ${d.triedSlugs.length ? d.triedSlugs.join(', ') : '(none)'}`)
  lines.push(`  config slug: ${d.configSlug ?? '(not cached — run `driftless login`)'}`)
  if (!d.meAttempted) {
    lines.push('  /me:         not attempted')
  } else if (d.meReturned) {
    lines.push(`  /me:         returned slug=${d.meSlug ?? '(none)'} workspace_id=${d.meWorkspaceId ?? '(none)'}`)
  } else {
    lines.push(`  /me:         FAILED — ${d.meError ?? 'unknown error'}`)
  }
  if (d.failedEndpoint) lines.push(`  last endpoint: ${d.failedEndpoint} (rejected)`)
  return lines.join('\n')
}

type RepoEntry = { id: string; github_org: string; github_repo: string }

async function fetchRepos(slug: string): Promise<{ repos: RepoEntry[] } | { error: string }> {
  try {
    // Same hardening as the /me call: a transient socket hang / rate-limit /
    // cold-start must not nuke workspace resolution. Without this, a burst of
    // commands (e.g. an E2E run) could trip every candidate into `error` and
    // collapse to a false `no_workspace` even though the config slug is good.
    const raw = await api.get(`/workspaces/${slug}/repos`, { timeoutMs: 8000, retries: 1 })
    if (Array.isArray(raw)) return { repos: raw as RepoEntry[] }
    return { error: 'unexpected response shape' }
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) }
  }
}

export async function resolveRepo(): Promise<RepoResolution> {
  const remote = getGitRemote()
  if (!remote) return { ok: false, reason: 'no_remote' }

  const configSlug = getCachedWorkspace().slug

  // /me is the authoritative source. Give it a timeout + one retry so a
  // transient socket hang doesn't silently nuke workspace identity.
  let meSlug: string | undefined
  let meWorkspaceId: string | undefined
  let meReturned = false
  let meError: string | undefined
  try {
    const me = (await api.get('/me', { timeoutMs: 8000, retries: 1 })) as {
      slug?: string
      workspace_id?: string
    }
    meReturned = true
    meSlug = me?.slug
    meWorkspaceId = me?.workspace_id
    if (meSlug) saveWorkspaceToConfig(meSlug, meWorkspaceId)
  } catch (e) {
    meError = e instanceof Error ? e.message : String(e)
  }

  // Priority: cached config slug → /me slug → git org (last resort; only ever
  // resolves if the workspace slug genuinely equals the org).
  const candidates = [...new Set([configSlug, meSlug, remote.org].filter(Boolean))] as string[]

  let lastLinkedSlug: string | undefined
  let failedEndpoint: string | undefined

  for (const slug of candidates) {
    const result = await fetchRepos(slug)
    if ('error' in result) {
      failedEndpoint = `/workspaces/${slug}/repos`
      continue
    }

    const repo = result.repos.find(r => r.github_org === remote.org && r.github_repo === remote.repo)
    if (repo) {
      // Cache whatever slug actually worked for next time.
      saveWorkspaceToConfig(slug, meWorkspaceId)
      const source: ResolutionSource =
        slug === configSlug ? 'config' : slug === meSlug ? 'me' : 'git-org'
      return {
        ok: true,
        workspaceSlug: slug,
        repoId: repo.id,
        remote,
        source,
      }
    }
    lastLinkedSlug = slug
  }

  const diagnostics: RepoDiagnostics = {
    remote,
    triedSlugs: candidates,
    configSlug,
    meAttempted: true,
    meReturned,
    meSlug,
    meWorkspaceId,
    meError,
    failedEndpoint,
  }

  if (lastLinkedSlug) {
    return { ok: false, reason: 'not_linked', workspaceSlug: lastLinkedSlug, remote, diagnostics }
  }

  return {
    ok: false,
    reason: 'no_workspace',
    remote,
    detail: formatDiagnostics(diagnostics),
    diagnostics,
  }
}
